/**
 * One fund in detail: the location × currency matrix, a balance chart, and the
 * fund's own transactions.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowLeft, ArrowLeftRight, FileText, ListOrdered, Loader2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/misc';
import { EmptyState, ErrorState, RowSkeleton } from '@/components/common';
import { TransactionRow } from '@/components/TransactionRow';
import { VoidDialog } from '@/components/VoidDialog';
import { QuickAddSheet } from '@/components/QuickAddSheet';
import { MovementDialog, type MovementMode } from '@/components/MovementDialog';
import { RepayLoanDialog } from '@/components/RepayLoanDialog';
import { LoansPanel } from '@/components/LoanCard';
import { MultiCurrency } from '@/components/Money';
import { colorClasses } from '@/lib/colors';
import { formatMoney, formatMonthShort, formatMonth } from '@/lib/format';
import {
  useBalanceHistory,
  useBalances,
  useFunds,
  useLoans,
  useTransactions,
} from '@/hooks/useApi';
import type { Loan, Transaction } from '@/lib/types';

export function FundDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const fundId = Number(id);

  const [voidTarget, setVoidTarget] = useState<Transaction | null>(null);
  const [quickAdd, setQuickAdd] = useState(false);
  const [movement, setMovement] = useState<MovementMode | null>(null);
  const [repayLoan, setRepayLoan] = useState<Loan | null>(null);

  const { data: funds = [] } = useFunds(true);
  const { data: balances, isLoading, error, refetch } = useBalances({ fundId });
  const { data: history } = useBalanceHistory({ months: 12, fundId });
  const { data: loans = [] } = useLoans({ fundId });
  const { data: txPages, isLoading: txLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useTransactions({ fundId, limit: 25 });

  const fund = funds.find((f) => f.id === fundId);
  const colors = colorClasses(fund?.color);

  const transactions = useMemo(() => txPages?.pages.flatMap((p) => p.items) ?? [], [txPages]);

  const chartData = useMemo(
    () =>
      (history?.points ?? []).map((p) => ({
        month: p.month,
        value: p.baseMinor / 10 ** (history?.baseDecimals ?? 2),
      })),
    [history],
  );

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  if (isLoading || !balances) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <RowSkeleton count={5} />
      </div>
    );
  }

  const fundBalance = balances.byFund[0];
  const { baseCurrency, baseDecimals } = balances;

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2 gap-1 text-muted-foreground">
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Начало
          </Link>
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className={cn('h-3 w-3 rounded-full', colors.bg)} />
            <h1 className="text-2xl font-semibold tracking-tight">{fund?.name ?? 'Каса'}</h1>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="gap-2" onClick={() => setQuickAdd(true)}>
              <Plus className="h-4 w-4" />
              Операция
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setMovement('transfer')}
            >
              <ArrowLeftRight className="h-4 w-4" />
              Прехвърли
            </Button>
            <Button variant="outline" size="sm" asChild className="gap-2">
              <Link to={`/funds/${fundId}/statement`}>
                <FileText className="h-4 w-4" />
                Отчет
              </Link>
            </Button>
          </div>
        </div>

        {fund?.note ? <p className="mt-1 text-sm text-muted-foreground">{fund.note}</p> : null}
      </div>

      {/* ── Balance ── */}
      <Card>
        <CardContent className="p-5">
          <p className="text-sm text-muted-foreground">Салдо</p>
          <div className="tabular mt-1 text-3xl font-semibold">
            {fundBalance ? (
              <MultiCurrency
                totals={fundBalance.byCurrency}
                baseMinor={fundBalance.baseMinor}
                baseCurrency={baseCurrency}
                baseDecimals={baseDecimals}
              />
            ) : (
              formatMoney(0, baseCurrency, baseDecimals)
            )}
          </div>

          {/* Location × currency matrix — the fund's own breakdown. */}
          {fundBalance && fundBalance.byLocation.length > 0 ? (
            <div className="mt-4 space-y-2 border-t pt-4">
              {fundBalance.byLocation.map((loc) => (
                <div key={loc.locationId} className="flex items-start justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="truncate">{loc.locationName}</span>
                    {loc.holderName ? (
                      <span className="text-muted-foreground"> · {loc.holderName}</span>
                    ) : null}
                  </span>
                  <MultiCurrency
                    totals={loc.byCurrency}
                    baseMinor={loc.baseMinor}
                    baseCurrency={baseCurrency}
                    baseDecimals={baseDecimals}
                    className="shrink-0 text-right"
                    compact
                  />
                </div>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <LoansPanel loans={loans.filter((l) => !l.closedAt)} onRepay={setRepayLoan} />

      {/* ── History ── */}
      {chartData.length > 1 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Движение за 12 месеца</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                  <defs>
                    <linearGradient id="fundArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--income))" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(var(--income))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickFormatter={formatMonthShort}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelFormatter={(v: string) => formatMonth(v)}
                    formatter={(v: number) => [
                      formatMoney(Math.round(v * 10 ** baseDecimals), baseCurrency, baseDecimals),
                      'Салдо',
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(var(--income))"
                    strokeWidth={2}
                    fill="url(#fundArea)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Графиката е по историческа стойност — показва реалните движения, а не промени от
              валутните курсове.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* ── Transactions ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Операции</CardTitle>
        </CardHeader>
        <CardContent>
          {txLoading ? (
            <RowSkeleton count={5} />
          ) : transactions.length === 0 ? (
            <EmptyState
              icon={ListOrdered}
              title="Няма операции в тази каса"
              action={<Button onClick={() => setQuickAdd(true)}>Добави операция</Button>}
            />
          ) : (
            <div className="space-y-2">
              {transactions.map((tx) => (
                <TransactionRow key={tx.id} tx={tx} onVoid={setVoidTarget} />
              ))}
              {hasNextPage ? (
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={isFetchingNextPage}
                  onClick={() => void fetchNextPage()}
                >
                  {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Зареди още
                </Button>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      <VoidDialog tx={voidTarget} onClose={() => setVoidTarget(null)} />
      <QuickAddSheet open={quickAdd} onOpenChange={setQuickAdd} defaultFundId={fundId} />
      <MovementDialog
        mode={movement}
        onClose={() => setMovement(null)}
        defaultFrom={{ fundId }}
      />
      <RepayLoanDialog loan={repayLoan} onClose={() => setRepayLoan(null)} />
    </div>
  );
}
